import React from 'react';

const KEYWORDS = /\b(import|from|export|default|class|extends|constructor|if|return|new|this|let|const|null|true|false)\b/;
const STRING_SINGLE = /^'(?:[^'\\]|\\.)*'/;
const STRING_DOUBLE = /^"(?:[^"\\]|\\.)*"/;
const STRING_BACK = /^`(?:[^`\\]|\\.)*`/;
const COMMENT = /^\/\/.*/;
const NUMBER = /^\d+/;

function tokenizeLine(line: string): { type: string; value: string }[] {
    const out: { type: string; value: string }[] = [];
    let i = 0;
    while (i < line.length) {
        const rest = line.slice(i);
        let m = rest.match(COMMENT);
        if (m) {
            out.push({ type: 'comment', value: m[0] });
            i += m[0].length;
            continue;
        }
        m = rest.match(STRING_SINGLE) || rest.match(STRING_DOUBLE) || rest.match(STRING_BACK);
        if (m) {
            out.push({ type: 'string', value: m[0] });
            i += m[0].length;
            continue;
        }
        m = rest.match(KEYWORDS);
        if (m) {
            out.push({ type: 'keyword', value: m[0] });
            i += m[0].length;
            continue;
        }
        m = rest.match(NUMBER);
        if (m) {
            out.push({ type: 'number', value: m[0] });
            i += m[0].length;
            continue;
        }
        const next = rest.search(/[\/'"`\d]|\b(?:import|from|export|default|class|extends|constructor|if|return|new|this|let|const|null|true|false)\b/);
        const end = next === -1 ? rest.length : next;
        if (end > 0) {
            out.push({ type: 'text', value: rest.slice(0, end) });
            i += end;
        } else {
            out.push({ type: 'text', value: rest[0] });
            i += 1;
        }
    }
    return out;
}

const tokenStyles: Record<string, React.CSSProperties> = {
    keyword: { color: '#c586c0' },
    string: { color: '#ce9178' },
    comment: { color: '#6a9955', fontStyle: 'italic' },
    number: { color: '#b5cea8' },
    text: { color: '#d4d4d4' },
};

export interface CodeBlockProps {
    code: string;
    style?: React.CSSProperties;
    lineNumbers?: boolean;
}

const CodeBlock: React.FC<CodeBlockProps> = ({ code, style, lineNumbers = true }) => {
    const lines = code.split('\n');
    return (
        <div style={{ ...blockStyle.wrapper, ...style }}>
            {lineNumbers && (
                <div style={blockStyle.lineNumbers} aria-hidden>
                    {lines.map((_, i) => (
                        <span key={i} style={blockStyle.lineNum}>{i + 1}</span>
                    ))}
                </div>
            )}
            <pre style={blockStyle.pre}>
                <code style={blockStyle.code}>
                    {lines.map((line, lineIdx) => (
                        <span key={lineIdx} style={{ display: 'block' }}>
                            {tokenizeLine(line).map((tok, i) => (
                                <span key={i} style={tokenStyles[tok.type] || tokenStyles.text}>
                                    {tok.value}
                                </span>
                            ))}
                            {lineIdx < lines.length - 1 ? '\n' : ''}
                        </span>
                    ))}
                </code>
            </pre>
        </div>
    );
};

const blockStyle: Record<string, React.CSSProperties> = {
    wrapper: {
        display: 'flex',
        fontFamily: 'ui-monospace, "SF Mono", Monaco, "Cascadia Code", monospace',
        fontSize: 12,
        lineHeight: 1.5,
        overflow: 'auto',
        background: '#1e1e1e',
        margin: 0,
        minHeight: 0,
    },
    lineNumbers: {
        flexShrink: 0,
        paddingTop: 12,
        paddingBottom: 12,
        paddingRight: 12,
        textAlign: 'right',
        color: '#858585',
        userSelect: 'none',
        minWidth: 32,
    },
    lineNum: {
        display: 'block',
        lineHeight: 1.5,
    },
    pre: {
        flex: 1,
        margin: 0,
        padding: '12px 16px',
        overflow: 'auto',
    },
    code: {
        whiteSpace: 'pre',
    },
};

export default CodeBlock;
